import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { fetchVisitedVenueIds } from '../attendedUtils';

// Normalize for search: strip diacritics and punctuation so "motorhead"
// matches "Motörhead" and "carissas" matches "Carissa's Wierd".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

const PAGE_SIZE = 25;
const NUMERIC_COLS = new Set(['capacity', 'total_shows']);
const DATE_COLS    = new Set(['last_show_date']);

// Shared between the desktop table headers and the mobile "Sort by" select,
// so both stay in sync automatically if columns ever change.
const SORT_COLUMNS = [
    { col: 'name',           label: 'Venue Name' },
    { col: 'city',           label: 'City' },
    { col: 'state',          label: 'State' },
    { col: 'country',        label: 'Country' },
    { col: 'venue_type',     label: 'Type' },
    { col: 'capacity',       label: 'Capacity' },
    { col: 'total_shows',    label: 'Shows' },
    { col: 'last_show_date', label: 'Most Recent Show' },
];

const VIEW_OPTIONS = [
    { value: 'list', label: 'Show Results in List View' },
    { value: 'map',  label: 'Show Results in Map View' },
];

function formatShowDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    });
}

// Geocoding "Yes" = lat AND lng both populated; anything else is "No"
// (SHARC_Spec_Venue_Browse_2026-09-24, decision #4). Also used to decide
// which venues get a pin in Map View, so the filter and the map always agree.
function isGeocoded(v) {
    const hasLat = v.lat !== null && v.lat !== undefined && String(v.lat).trim() !== '' && !isNaN(parseFloat(v.lat));
    const hasLng = v.lng !== null && v.lng !== undefined && String(v.lng).trim() !== '' && !isNaN(parseFloat(v.lng));
    return hasLat && hasLng;
}

// Term of shows for the map popup: a single year ("2024") when every show
// falls in one year, otherwise a range ("2024 – 2026"). Empty for 0 shows.
function formatShowTerm(firstDate, lastDate) {
    if (!firstDate || !lastDate) return '';
    const y1 = String(firstDate).slice(0, 4);
    const y2 = String(lastDate).slice(0, 4);
    return y1 === y2 ? y1 : `${y1} – ${y2}`;
}

// Venue names/cities go into Leaflet popup HTML strings, so escape them —
// a name containing "&" or "<" would otherwise break the popup markup.
function escapeHtml(str) {
    return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// ── Mobile card (replaces the table below 768px — see
// SHARC_Mobile_Responsive_Spec_v1.1, §5.3) ──────────────────────────────────
function VenueCardMobile({ venue }) {
    const location = [venue.city, venue.state, venue.country].filter(Boolean).join(', ');
    return (
        <Link to={`/venues/${venue.slug || venue.id}`} style={mobileStyles.card}>
            <div style={mobileStyles.cardName}>{venue.name}</div>
            {location && <div style={mobileStyles.cardLocation}>{location}</div>}
            {venue.venue_type && (
                <div style={mobileStyles.cardTags}>
                    <span style={mobileStyles.tag}>{venue.venue_type}</span>
                </div>
            )}
            <div style={mobileStyles.cardStatsRow}>
                <span>{venue.capacity ? `Capacity ${Number(venue.capacity).toLocaleString()}` : 'Capacity —'}</span>
                <span>{Number(venue.total_shows) || 0} shows</span>
                <span>{formatShowDate(venue.last_show_date)}</span>
            </div>
        </Link>
    );
}

// ── List / Map view dropdown — trigger styled like ActionsDropdown.js's
// ghostBtn, menu styled like its menu card (spec §6.2) ────────────────────────
function ViewModeDropdown({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        function onOut(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const current = VIEW_OPTIONS.find(o => o.value === value) || VIEW_OPTIONS[0];

    return (
        <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
            <button
                onClick={() => setOpen(v => !v)}
                style={{
                    ...S.ghostBtn,
                    color: '#bb86fc',
                    backgroundColor: open ? '#2c2c2c' : 'transparent',
                    borderColor: open ? '#555' : '#444',
                }}
            >
                {current.label} <span style={S.chevron}>{open ? '▴' : '▾'}</span>
            </button>
            {open && (
                <div style={S.viewMenu}>
                    {VIEW_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => { onChange(opt.value); setOpen(false); }}
                            style={{
                                ...S.viewMenuItem,
                                color: opt.value === value ? '#bb86fc' : '#e0e0e0',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2a2a2a'; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Map View — adapted from ArtistDetail.js's GeographicMapTab (spec §6.5).
// Unlike GeographicMapTab, the map instance is created once and only the
// marker layer is swapped when the filtered list changes, so typing in the
// search box doesn't tear down and rebuild the whole map on every keystroke.
function VenueMapView({ venues, isMobile }) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerLayerRef = useRef(null);
    const [leafletReady, setLeafletReady] = useState(!!window.L);

    const geocoded = useMemo(() => venues.filter(isGeocoded), [venues]);

    // Load Leaflet (same source/version/link id as GeographicMapTab)
    useEffect(() => {
        if (window.L) { setLeafletReady(true); return; }
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css'; link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
        }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => setLeafletReady(true);
        document.head.appendChild(script);
    }, []);

    // Create the map once, tear it down on unmount (e.g. switching to List View)
    useEffect(() => {
        if (!leafletReady || !mapRef.current || mapInstanceRef.current) return;
        const L = window.L;
        const map = L.map(mapRef.current, { zoomControl: true, worldCopyJump: true });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '© OpenStreetMap contributors', maxZoom: 18 }).addTo(map);
        map.setView([20, 0], 2);
        mapInstanceRef.current = map;
        markerLayerRef.current = L.layerGroup().addTo(map);
        return () => {
            map.remove();
            mapInstanceRef.current = null;
            markerLayerRef.current = null;
        };
    }, [leafletReady]);

    // Swap pins + re-fit whenever the filtered list changes
    useEffect(() => {
        const map = mapInstanceRef.current;
        const layer = markerLayerRef.current;
        if (!leafletReady || !map || !layer) return;
        const L = window.L;
        layer.clearLayers();
        if (geocoded.length === 0) return;

        // The container may have just gone from hidden (0 results) to visible
        map.invalidateSize();

        const markerIcon = L.divIcon({ className: '', html: `<div style="width:12px;height:12px;background:#bb86fc;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.5);"></div>`, iconSize: [12, 12], iconAnchor: [6, 6], popupAnchor: [0, -8] });
        const bounds = [];
        geocoded.forEach(v => {
            const lat = parseFloat(v.lat); const lng = parseFloat(v.lng);
            bounds.push([lat, lng]);
            const locationStr = [v.city, v.state, v.country].filter(Boolean).join(', ');
            const showCount = Number(v.total_shows) || 0;
            const term = showCount > 0 ? formatShowTerm(v.first_show_date, v.last_show_date) : '';
            const popupHtml =
                `<div style="font-family:sans-serif;min-width:180px;max-width:260px;">` +
                    `<div style="font-weight:bold;font-size:14px;margin-bottom:4px;">` +
                        `<a href="/venues/${encodeURIComponent(v.slug || v.id)}" target="_blank" rel="noopener noreferrer" style="color:#bb86fc;text-decoration:none;">${escapeHtml(v.name)} ↗</a>` +
                    `</div>` +
                    (locationStr ? `<div style="font-size:12px;color:#888;margin-bottom:6px;">${escapeHtml(locationStr)}</div>` : '') +
                    `<div style="font-size:12px;color:#aaa;margin-bottom:${term ? '4px' : '0'};"><strong style="color:#e0e0e0;">${showCount.toLocaleString()}</strong> show${showCount !== 1 ? 's' : ''}</div>` +
                    (term ? `<div style="font-size:12px;color:#999;">${term}</div>` : '') +
                `</div>`;
            L.marker([lat, lng], { icon: markerIcon }).bindPopup(popupHtml, { maxWidth: 280 }).addTo(layer);
        });
        if (bounds.length === 1) map.setView(bounds[0], 12);
        else map.fitBounds(bounds, { padding: [40, 40] });
    }, [leafletReady, geocoded]);

    const mapHeight = isMobile ? '380px' : '480px';

    return (
        <div>
            <div style={S.mapNote}>
                {geocoded.length.toLocaleString()} of {venues.length.toLocaleString()} venue{venues.length !== 1 ? 's' : ''} have location data · Click a pin for details
            </div>
            {!leafletReady && <div style={S.message}>Loading map...</div>}
            {leafletReady && geocoded.length === 0 && (
                <div style={S.message}>No venues with location data match your filters.</div>
            )}
            {/* Kept mounted (just hidden) when there are no pins, so the map
                instance survives a filter change that briefly empties it.
                position/zIndex isolate Leaflet's internal z-indexes (up to
                1000) so its panes never paint over dropdown menus. */}
            <div
                ref={mapRef}
                style={{
                    ...S.mapBox,
                    height: mapHeight,
                    display: leafletReady && geocoded.length > 0 ? 'block' : 'none',
                }}
            />
        </div>
    );
}

function VenueList() {
    const isMobile = useIsMobile();
    const [venues, setVenues]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Picklists for code→name lookup in filter dropdowns
    const [picklists, setPicklists] = useState(null);

    // Filters
    const [search, setSearch]               = useState('');
    const [filterVisited, setFilterVisited] = useState('');
    const [filterType, setFilterType]       = useState('');
    const [filterGeo, setFilterGeo]         = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterState, setFilterState]     = useState('');
    const [dateFrom, setDateFrom]           = useState('');
    const [dateTo, setDateTo]               = useState('');

    // Visited/Not Visited data (SHARC_Spec_Venue_Browse_2026-09-24) — best-effort,
    // authenticated bulk fetch, same non-blocking pattern as ArtistList.js's
    // Seen filter. Left empty/false on any failure, including being logged out.
    const [visitedVenueIds, setVisitedVenueIds] = useState(new Set());
    const [isLoggedIn, setIsLoggedIn]           = useState(false);

    // Sort + page + view
    const [sortCol, setSortCol]   = useState('name');
    const [sortDir, setSortDir]   = useState('asc');
    const [page, setPage]         = useState(1);
    const [viewMode, setViewMode] = useState('list');

    useEffect(() => {
        document.title = 'SHARC - Venues';
        Promise.all([
            axios.get(`${config.API_URL}/venues`),
            axios.get(`${config.API_URL}/picklists`)
        ])
            .then(([venuesRes, picklistsRes]) => {
                setVenues(venuesRes.data);
                setPicklists(picklistsRes.data);
                setLoading(false);
            })
            .catch(() => { setError('Failed to load venues'); setLoading(false); });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    // Best-effort — silently leaves visitedVenueIds empty / isLoggedIn false for
    // logged-out visitors or any fetch failure.
    useEffect(() => {
        fetchVisitedVenueIds()
            .then(ids => { setVisitedVenueIds(new Set(ids)); setIsLoggedIn(true); })
            .catch(() => { /* not logged in, or fetch failed — leave defaults */ });
    }, []);

    // code→name lookup maps built from picklists
    const countryNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.countries.map(c => [c.code, c.name]));
    }, [picklists]);

    // Key: "countryId|stateCode" — avoids collisions where two countries share a code (e.g. CO)
    const stateNameMap = useMemo(() => {
        if (!picklists) return {};
        return Object.fromEntries(picklists.states_regions.map(s => [`${s.country_id}|${s.code}`, s.name]));
    }, [picklists]);

    // Resolve the picklist country_id for the currently selected country code
    const selectedCountryPicklistId = useMemo(() => {
        if (!picklists || !filterCountry) return null;
        const match = picklists.countries.find(c => c.code === filterCountry);
        return match ? match.id : null;
    }, [picklists, filterCountry]);

    // Unique filter options (still using codes as values)
    const types     = useMemo(() => [...new Set(venues.map(v => v.venue_type).filter(Boolean))].sort(), [venues]);
    const countries = useMemo(() => {
        const unique = [...new Set(venues.map(v => v.country).filter(Boolean))];
        return unique.sort((a, b) => (countryNameMap[a] || a).localeCompare(countryNameMap[b] || b));
    }, [venues, countryNameMap]);
    const states    = useMemo(() => {
        const src = filterCountry ? venues.filter(v => v.country === filterCountry) : venues;
        return [...new Set(src.map(v => v.state).filter(Boolean))].sort();
    }, [venues, filterCountry]);

    const handleSort = (col) => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    };

    const resetPage = () => setPage(1);

    const clearFilters = () => {
        setSearch(''); setFilterVisited(''); setFilterType(''); setFilterGeo('');
        setFilterCountry(''); setFilterState('');
        setDateFrom(''); setDateTo('');
        setPage(1);
    };

    const hasFilters = search || filterVisited || filterType || filterGeo || filterCountry || filterState || dateFrom || dateTo;

    const filtered = useMemo(() => {
        const q = normalizeStr(search);
        return venues.filter(v => {
            if (search && !((v.name && normalizeStr(v.name).includes(q)) || (v.city && normalizeStr(v.city).includes(q)))) return false;
            // Logged-out + Visited or Not Visited selected → zero results (explicit
            // check rather than relying on an empty visitedVenueIds set, which
            // would make "Not Visited" incorrectly match every venue).
            if (filterVisited && !isLoggedIn) return false;
            if (filterVisited === 'visited'     && !visitedVenueIds.has(v.id)) return false;
            if (filterVisited === 'not_visited' &&  visitedVenueIds.has(v.id)) return false;
            if (filterType    && v.venue_type !== filterType)    return false;
            if (filterGeo === 'yes' && !isGeocoded(v)) return false;
            if (filterGeo === 'no'  &&  isGeocoded(v)) return false;
            if (filterCountry && v.country    !== filterCountry) return false;
            if (filterState   && v.state      !== filterState)   return false;
            if (dateFrom && (!v.last_show_date || v.last_show_date.slice(0, 10) < dateFrom)) return false;
            if (dateTo   && (!v.last_show_date || v.last_show_date.slice(0, 10) > dateTo))   return false;
            return true;
        });
    }, [venues, search, filterVisited, isLoggedIn, visitedVenueIds, filterType, filterGeo, filterCountry, filterState, dateFrom, dateTo]);

    const sorted = useMemo(() => {
        return [...filtered].sort((a, b) => {
            const av = NUMERIC_COLS.has(sortCol) ? Number(a[sortCol] ?? 0) : (a[sortCol] ?? '');
            const bv = NUMERIC_COLS.has(sortCol) ? Number(b[sortCol] ?? 0) : (b[sortCol] ?? '');
            const cmp = NUMERIC_COLS.has(sortCol)
                ? av - bv
                : DATE_COLS.has(sortCol)
                    ? (av || '').localeCompare(bv || '')
                    : String(av).localeCompare(String(bv));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [filtered, sortCol, sortDir]);

    const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
    const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const isMapView  = viewMode === 'map';

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span style={S.sortIcon}>↕</span>;
        return <span style={{ ...S.sortIcon, color: '#bb86fc' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    if (loading) return <div style={S.message}>Loading venues...</div>;
    if (error)   return <div style={S.errMsg}>{error}</div>;

    return (
        <div>
            {/* ── Header ── */}
            <div style={isMobile ? { ...S.header, ...Smobile.header } : S.header}>
                <h1 style={S.title}>Venues</h1>
                <div style={isMobile ? { ...S.headerRight, ...Smobile.headerRight } : S.headerRight}>
                    <span style={S.count}>{sorted.length.toLocaleString()} of {venues.length.toLocaleString()}</span>
                    <Link to="/add-venue" style={S.addButton}>+ Add Venue</Link>
                    <ViewModeDropdown value={viewMode} onChange={setViewMode} />
                </div>
            </div>

            {/* ── Filters Row 1: text search + Most Recent Show date range ── */}
            <div style={S.filterBar}>
                <input
                    type="text"
                    placeholder="Search by venue name or city..."
                    value={search}
                    onChange={e => { setSearch(e.target.value); resetPage(); }}
                    style={S.searchInput}
                />
                <span style={S.dateLabel}>Most Recent Show</span>
                <input
                    type="date"
                    value={dateFrom}
                    onChange={e => { setDateFrom(e.target.value); resetPage(); }}
                    style={S.dateInput}
                />
                <span style={S.capDash}>–</span>
                <input
                    type="date"
                    value={dateTo}
                    onChange={e => { setDateTo(e.target.value); resetPage(); }}
                    style={S.dateInput}
                />
                {hasFilters && (
                    <button onClick={clearFilters} style={S.clearBtn}>✕ Clear all</button>
                )}
            </div>

            {/* ── Filters Row 2: dropdowns (same five-slot layout as ArtistList.js) ── */}
            <div style={S.filterBar}>
                <select value={filterVisited} onChange={e => { setFilterVisited(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">Visited or Not Visited</option>
                    <option value="visited">Visited</option>
                    <option value="not_visited">Not Visited</option>
                </select>
                <select value={filterType} onChange={e => { setFilterType(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Types</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={filterGeo} onChange={e => { setFilterGeo(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">Geocoding</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                </select>
                <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterState(''); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }}>
                    <option value="">All Countries</option>
                    {countries.map(c => (
                        <option key={c} value={c}>{countryNameMap[c] || c}</option>
                    ))}
                </select>
                <select value={filterState} onChange={e => { setFilterState(e.target.value); resetPage(); }} style={{ ...S.filterSelect, flex: '1 1 140px' }} disabled={states.length === 0}>
                    <option value="">All States</option>
                    {states.map(s => (
                        <option key={s} value={s}>{stateNameMap[`${selectedCountryPicklistId}|${s}`] || s}</option>
                    ))}
                </select>
            </div>

            {isMapView ? (
                /* ── Map View (pagination + mobile sort hidden; filters stay live) ── */
                <VenueMapView venues={filtered} isMobile={isMobile} />
            ) : (
                <>
                    {/* ── Mobile-only sort control (table headers below handle this on desktop) ── */}
                    {isMobile && (
                        <div style={mobileStyles.sortRow}>
                            <select
                                value={sortCol}
                                onChange={e => { setSortCol(e.target.value); resetPage(); }}
                                style={mobileStyles.sortSelect}
                            >
                                {SORT_COLUMNS.map(({ col, label }) => (
                                    <option key={col} value={col}>Sort: {label}</option>
                                ))}
                            </select>
                            <button
                                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                                style={mobileStyles.sortDirBtn}
                                aria-label={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                            >
                                {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
                            </button>
                        </div>
                    )}

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <div style={S.pagination}>
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                style={{ ...S.pageBtn, ...(page === 1 ? S.pageBtnDisabled : {}) }}
                            >← Prev</button>
                            <div style={S.pageNumbers}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                    .reduce((acc, p, i, arr) => {
                                        if (i > 0 && p - arr[i - 1] > 1) acc.push('...');
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, i) => p === '...'
                                        ? <span key={`e-${i}`} style={S.ellipsis}>…</span>
                                        : <button key={p} onClick={() => setPage(p)} style={{ ...S.pageBtn, ...(p === page ? S.pageBtnActive : {}) }}>{p}</button>
                                    )
                                }
                            </div>
                            <button
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                style={{ ...S.pageBtn, ...(page === totalPages ? S.pageBtnDisabled : {}) }}
                            >Next →</button>
                            <span style={S.pageInfo}>
                                {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, sorted.length)} of {sorted.length.toLocaleString()}
                            </span>
                        </div>
                    )}

                    {/* ── Table ── */}
                    {sorted.length === 0 ? (
                        <div style={S.message}>
                            {hasFilters ? 'No venues match your filters.' : 'No venues yet.'}
                        </div>
                    ) : isMobile ? (
                        <div style={mobileStyles.cardList}>
                            {paginated.map((v) => (
                                <VenueCardMobile key={v.id} venue={v} />
                            ))}
                        </div>
                    ) : (
                        <div style={S.tableContainer}>
                            <table style={S.table}>
                                <thead>
                                    <tr>
                                        {SORT_COLUMNS.map(({ col, label }) => (
                                            <th
                                                key={col}
                                                style={{ ...S.th, cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort(col)}
                                            >
                                                {label} <SortIcon col={col} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.map((v, idx) => (
                                        <tr
                                            key={v.id}
                                            style={idx % 2 === 0 ? S.trEven : S.trOdd}
                                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#252525'}
                                            onMouseLeave={e => e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#1e1e1e' : '#1a1a1a'}
                                        >
                                            <td style={S.td}>
                                                <Link to={`/venues/${v.slug || v.id}`} style={S.link}>{v.name}</Link>
                                            </td>
                                            <td style={S.td}>{v.city || '—'}</td>
                                            <td style={S.td}>{v.state || '—'}</td>
                                            <td style={S.td}>{v.country || '—'}</td>
                                            <td style={S.td}>{v.venue_type || '—'}</td>
                                            <td style={{ ...S.td, ...S.numCell }}>{v.capacity ? Number(v.capacity).toLocaleString() : '—'}</td>
                                            <td style={{ ...S.td, ...S.numCell }}>{Number(v.total_shows) || 0}</td>
                                            <td style={{ ...S.td, ...S.numCell }}>{formatShowDate(v.last_show_date)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const S = {
    header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title:        { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    headerRight:  { display: 'flex', alignItems: 'center', gap: '16px' },
    count:        { fontSize: '13px', color: '#666' },
    addButton:    { backgroundColor: '#bb86fc', color: '#121212', textDecoration: 'none', padding: '8px 18px', borderRadius: '4px', fontWeight: 'bold', fontSize: '14px' },

    // View dropdown — ghostBtn/chevron/menu copied from ActionsDropdown.js
    ghostBtn:     { background: 'transparent', border: '1px solid #444', borderRadius: '4px', color: '#ccc', fontSize: '14px', padding: '7px 13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' },
    chevron:      { fontSize: '10px', opacity: 0.75 },
    viewMenu:     { position: 'absolute', top: 'calc(100% + 5px)', right: 0, left: 'auto', backgroundColor: '#1e1e1e', border: '1px solid #444', borderRadius: '6px', minWidth: '220px', zIndex: 300, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', overflow: 'hidden' },
    viewMenuItem: { display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', fontSize: '14px', fontWeight: 'bold', background: 'transparent', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' },

    // Filter rows — spacing, date label/input and Clear all copied from ArtistList.js
    filterBar:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'center' },
    searchInput:  { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 14px', fontSize: '14px', color: '#e0e0e0', flex: '1 1 260px', minWidth: '200px', boxSizing: 'border-box' },
    filterSelect: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', color: '#e0e0e0', flex: '0 1 140px', minWidth: '110px', boxSizing: 'border-box' },
    dateLabel:    { fontSize: '12px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' },
    dateInput:    { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', color: '#e0e0e0', width: '130px', boxSizing: 'border-box' },
    capDash:      { color: '#555', fontSize: '14px' },
    clearBtn:     { backgroundColor: 'transparent', color: '#888', border: '1px solid #444', borderRadius: '6px', padding: '9px 12px', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },

    pagination:      { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' },
    pageNumbers:     { display: 'flex', gap: '4px', alignItems: 'center' },
    pageBtn:         { backgroundColor: '#2c2c2c', color: '#e0e0e0', border: '1px solid #444', borderRadius: '4px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer', minWidth: '36px', textAlign: 'center' },
    pageBtnActive:   { backgroundColor: '#bb86fc', color: '#121212', border: '1px solid #bb86fc', fontWeight: 700 },
    pageBtnDisabled: { opacity: 0.35, cursor: 'default' },
    ellipsis:        { color: '#555', padding: '0 4px', fontSize: '13px' },
    pageInfo:        { fontSize: '12px', color: '#666', marginLeft: '8px' },

    tableContainer: { overflowX: 'auto' },
    table:          { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th:             { textAlign: 'left', padding: '10px 14px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td:             { padding: '10px 14px', color: '#e0e0e0', borderBottom: '1px solid #222', whiteSpace: 'nowrap' },
    trEven:         { backgroundColor: '#1e1e1e' },
    trOdd:          { backgroundColor: '#1a1a1a' },
    link:           { color: '#bb86fc', textDecoration: 'none' },
    numCell:        { textAlign: 'right', color: '#aaa', fontVariantNumeric: 'tabular-nums' },
    sortIcon:       { fontSize: '10px', marginLeft: '4px', color: '#555' },
    message:        { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    errMsg:         { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },

    // Map View — note/box styling matches ArtistDetail.js's GeographicMapTab
    mapNote:        { marginBottom: '12px', fontSize: '13px', color: '#888' },
    mapBox:         { borderRadius: '8px', border: '1px solid #333', overflow: 'hidden', position: 'relative', zIndex: 0 },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_v1.1, §4.3/§5.3) ──
const Smobile = {
    // "Venues" heading + count/+Add/view-dropdown group can overflow a nowrap
    // flex row on narrow viewports — allow it to wrap onto a second line instead.
    header: {
        flexWrap: 'wrap',
        gap: '10px',
    },
    // Three items in the right group (count, + Add Venue, view dropdown) —
    // let them wrap within the group too rather than forcing page overflow.
    headerRight: {
        flexWrap: 'wrap',
        gap: '10px',
    },
};

// Kept as a separate object, only used when useIsMobile() is true, so the
// desktop table above is completely untouched.
const mobileStyles = {
    sortRow: {
        display: 'flex',
        gap: '8px',
        marginBottom: '16px',
    },
    sortSelect: {
        flex: '1 1 auto',
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 12px',
        fontSize: '13px',
        color: '#e0e0e0',
        boxSizing: 'border-box',
    },
    sortDirBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '6px',
        padding: '9px 14px',
        fontSize: '13px',
        color: '#e0e0e0',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    cardList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    card: {
        display: 'block',
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        padding: '14px 16px',
        textDecoration: 'none',
        color: '#e0e0e0',
    },
    cardName: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: '#bb86fc',
        marginBottom: '4px',
    },
    cardLocation: {
        fontSize: '13px',
        color: '#ccc',
        marginBottom: '8px',
    },
    cardTags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '8px',
    },
    tag: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '2px 8px',
        fontSize: '12px',
        color: '#bb86fc',
        fontWeight: 'bold',
    },
    cardStatsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: '8px',
        fontSize: '12px',
        color: '#aaa',
        paddingTop: '8px',
        borderTop: '1px solid #2a2a2a',
    },
};

export default VenueList;
